import { useMemo } from "react";

import type { Project } from "../../types";
import {
  type ProviderCredentialMode,
  providerPolicyForAccess,
  useForgeProviderAccess,
} from "../../cmmd/forgeProviderAccess";
import { useCmmdRuntimeInventorySnapshot } from "../../cmmd/cmmdRuntimeInventoryResource";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { getStoredLocalDeviceRuntimeId } from "../../deviceRegistryIdentity";
import { resolveLocalDeviceRuntimeId } from "../../environments/localExecutionEnvironment";
import { usePrimaryEnvironmentDeviceRuntimeId } from "../../environments/primary/context";
import { useObserveActiveRouteCloudRuntimeConnection } from "../../routes/ensureActiveRouteCloudRuntime";
import type { ActiveProjectPresentation } from "./activeProjectPresentation";
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { DesktopRuntimeInventoryProjectSelection } from "./NewThreadLanding.submission";
import {
  type DesktopRuntimeCatalog,
  resolveDesktopRuntimeProjectSelection,
} from "./useDesktopRuntimeCatalog";
import { useDraftModelSelection } from "./useDraftModelSelection";
import { useDraftProviderInventory } from "./useDraftProviderInventory";
import { useShellProviderAvailability } from "../../cmmd/useShellProviderAvailability";
import { resolveProvisioningProviderCatalog } from "./provisioningProviderCatalog";

export interface DraftExecutionTargetInput {
  readonly activeProject: Project | null;
  readonly activeProjectPresentation: ActiveProjectPresentation;
  readonly activeCloudCatalogRow: CloudProjectCatalogRow | null;
  readonly desktopRuntimeCatalog: DesktopRuntimeCatalog;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryServerConfig: Parameters<
    typeof useDraftProviderInventory
  >[0]["primaryServerConfig"];
  readonly providerCredentialMode: ProviderCredentialMode;
}

/**
 * What will run this turn, and what it can run.
 *
 * One hook because it is one chain, and every link is the previous link's only
 * consumer: the selected project names a target environment, the target
 * environment either publishes a runtime inventory or inherits the known
 * provision-time catalog, that state decides which providers are eligible, and
 * eligibility decides which model is selected. Held apart, each of those was a
 * hook in the landing component whose only purpose was to feed the next one.
 */
export function useDraftExecutionTarget(input: DraftExecutionTargetInput) {
  const targetEnvironmentId = input.activeProjectPresentation.environmentId;
  // Claude readiness is per runtime, so asking about none fails the check and
  // drops Claude out of the picker. The row is not the only witness: a
  // connected Forge Project carries no CMMD identity, so no row resolves for
  // it, while the environment id names the runtime either way.
  const providerAccess = useForgeProviderAccess({
    enabled: true,
    credentialMode: input.providerCredentialMode,
    runtimeId:
      input.activeCloudCatalogRow?.runtimeId ??
      classifyEnvironmentId(targetEnvironmentId).legacyRuntimeId,
  });
  const orgProviderPolicy = providerPolicyForAccess(providerAccess);
  const activeDesktopInventoryProject =
    useMemo<DesktopRuntimeInventoryProjectSelection | null>(() => {
      return resolveDesktopRuntimeProjectSelection({
        activeProjectPresent: input.activeProject !== null,
        summary: input.activeProjectPresentation.summary,
        catalog: input.desktopRuntimeCatalog,
      });
    }, [input.activeProject, input.activeProjectPresentation.summary, input.desktopRuntimeCatalog]);

  const targetEnvironmentRuntime = useSavedEnvironmentRuntimeStore((state) =>
    targetEnvironmentId ? (state.byId[targetEnvironmentId] ?? null) : null,
  );
  // Opening the picker is a Passive Read. Provider inventory can arrive from
  // durable replication, but only the later send action may connect or wake
  // this Environment.
  useObserveActiveRouteCloudRuntimeConnection(targetEnvironmentId);
  /**
   * The deadlock breaker. A STOPPED runtime is never connected by the resolver
   * above, so it publishes nothing and the picker had no models to offer —
   * leaving the user to send a turn blind in order to discover which model they
   * could have picked for it. The control plane kept the last catalog this
   * runtime published, so read that instead of waking anything.
   */
  const runtimeInventorySnapshot = useCmmdRuntimeInventorySnapshot();
  // Subscribed, not read once: the descriptor arrives after bootstrap and `/new`
  // paints before it lands, so a snapshot read would leave the picker holding
  // the first render's null with nothing to repaint it.
  const serverReportedDeviceRuntimeId = usePrimaryEnvironmentDeviceRuntimeId();
  const { providerInventory, providerStatuses } = useDraftProviderInventory({
    targetEnvironmentId,
    primaryEnvironmentId: input.primaryEnvironmentId,
    primaryServerConfig: input.primaryServerConfig,
    targetEnvironmentServerConfig: targetEnvironmentRuntime?.serverConfig ?? null,
    runtimes: runtimeInventorySnapshot.inventory?.runtimes ?? [],
    // Run on names this machine by its device runtime id, not by the primary
    // Environment id, so without this the resolver treats the local server as
    // a remote desktop it has never connected to (FOR-36439). The server's own
    // answer leads: page storage holds this desktop's id only inside the
    // desktop shell, and holds a per-browser id everywhere else.
    localDeviceRuntimeId: resolveLocalDeviceRuntimeId({
      serverReported: serverReportedDeviceRuntimeId,
      stored: getStoredLocalDeviceRuntimeId(),
    }),
  });
  const runtimeInventoryKnown = providerInventory.pickerProviderProps.providers !== undefined;
  const provisioningProviderCatalog = useMemo(
    () =>
      input.providerCredentialMode === "cmmd-cloud" && !runtimeInventoryKnown
        ? resolveProvisioningProviderCatalog(providerAccess)
        : null,
    [input.providerCredentialMode, providerAccess, runtimeInventoryKnown],
  );
  /**
   * Why the picker may withhold each provider it shows.
   *
   * One answer per rail row, joined from the Offer, the person's authority and
   * the Provider Inventory. This used to be two functions that each produced an
   * entry only for a provider something had already dropped, so a provider that
   * was never published carried no reason and the rail said "Coming soon."
   *
   * The inventory is passed unfiltered on purpose: a Withheld Provider stays on
   * the rail with its reason instead of disappearing.
   */
  const providerAvailability = useShellProviderAvailability({
    providers: providerInventory.pickerProviderProps.providers ?? [],
    policy: orgProviderPolicy,
    access: providerAccess,
    credentialMode: input.providerCredentialMode,
    hasLiveInventory: providerInventory.pickerProviderProps.providers !== undefined,
  });

  const {
    selectedModelSelection,
    resolvedSelectedProvider,
    selectedModel,
    modelOptionsByProvider,
    selectProviderModel,
    selectProviderAccount,
  } = useDraftModelSelection({
    providerStatuses,
    providerAccess,
    providerCredentialMode: input.providerCredentialMode,
    orgProviderPolicy,
    runtimeInventoryKnown,
    provisioningProviderCatalog,
  });

  // Named rather than spread: `useDraftModelSelection` also owns the raw
  // per-provider selection state, which is its own business — re-exporting it
  // would offer the landing component a second way to change the model that
  // bypasses the eligibility rules applied here.
  return {
    providerAccess,
    orgProviderPolicy,
    activeDesktopInventoryProject,
    providerInventory,
    providerAvailability,
    selectedModelSelection,
    resolvedSelectedProvider,
    selectedModel,
    modelOptionsByProvider,
    selectProviderModel,
    selectProviderAccount,
  };
}
